import { useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getTrendingGifs, searchGifs, type GiphyGif } from '@/services/giphy';

interface Props {
  onSelect: (gif: GiphyGif) => void;
  onClose: () => void;
}

/** GIF picker popover — trending by default, debounced search. */
export default function GifPickerPanel({ onSelect, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [gifs, setGifs] = useState<GiphyGif[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const timer = setTimeout(async () => {
      try {
        const res = query.trim() ? await searchGifs(query.trim()) : await getTrendingGifs();
        if (!cancelled) setGifs(res.data);
      } catch (e: any) {
        if (!cancelled)
          setError(e?.name === 'GiphyRateLimitError' ? 'Too many requests — try again shortly.' : 'Could not load GIFs.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, query ? 350 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  return (
    <div
      ref={panelRef}
      className="absolute bottom-full left-0 z-30 mb-2 flex h-96 w-96 flex-col rounded-xl border border-flock-gray-200 bg-white shadow-2xl"
    >
      <div className="flex items-center gap-2 border-b border-flock-gray-200 p-2">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-flock-navy-300" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search GIFs"
            className="w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 py-2 pl-9 pr-3 text-sm outline-none focus:border-flock-navy-400"
          />
        </div>
        <button onClick={onClose} className="p-1.5 text-flock-navy-400 hover:text-flock-navy-600">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {loading && <div className="p-4 text-center text-sm text-flock-navy-400">Loading…</div>}
        {error && <div className="p-4 text-center text-sm text-red-600">{error}</div>}
        {!loading && !error && (
          <div className="columns-2 gap-2">
            {gifs.map((gif) => (
              <button
                key={gif.id}
                onClick={() => onSelect(gif)}
                className="mb-2 block w-full overflow-hidden rounded-lg"
                title={gif.title}
              >
                <img
                  src={gif.images.fixed_height.url}
                  alt={gif.title}
                  loading="lazy"
                  className="w-full"
                />
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="border-t border-flock-gray-200 px-3 py-1 text-right text-[10px] text-flock-navy-300">
        Powered by GIPHY
      </div>
    </div>
  );
}
